import { useEffect, useRef, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const deliverables = [
  {
    icon: 'ri-palette-line',
    title: 'Dizajn na mieru',
    text: 'Žiadne šablóny. Každý web navrhujeme od nuly podľa vašej značky, zákazníkov a cieľov.',
    highlight: 'Unikátny dizajn',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Perfektný na mobile',
    text: 'Web vyzerá skvele na každom zariadení — telefón, tablet, počítač. Bez kompromisov.',
    highlight: 'Responzívny dizajn',
  },
  {
    icon: 'ri-search-2-line',
    title: 'SEO základy v cene',
    text: 'Správna štruktúra, rýchlosť a technické nastavenia, vďaka ktorým vás Google rád zobrazuje.',
    highlight: 'Viditeľnosť na Googli',
  },
  {
    icon: 'ri-pencil-ruler-2-line',
    title: 'Spravujete sami',
    text: 'Texty, fotky, novinky — to zvládnete sami cez jednoduché rozhranie. Bez programátora.',
    highlight: 'Jednoduchá správa',
  },
  {
    icon: 'ri-speed-up-line',
    title: 'Rýchle načítanie',
    text: 'Optimalizovaný kód a obrázky. Váš web sa načíta rýchlo — zákazníci nečakajú.',
    highlight: 'Výkon &lt; 2 sekundy',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'SSL a bezpečnosť',
    text: 'Zabezpečený certifikát, ochrana pred útokmi a pravidelné zálohy. Váš web je v bezpečí.',
    highlight: 'Plná bezpečnosť',
  },
  {
    icon: 'ri-cursor-fill',
    title: 'Navrhnutý na konverzie',
    text: 'Každý prvok vedie zákazníka k akcii — zavolať, napísať, objednať. Nie len pekne vyzerať.',
    highlight: 'Viac zákazníkov',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Podpora po odovzdaní',
    text: 'Nie sme tu len do spustenia. Pomôžeme s úpravami, otázkami a technickými problémami.',
    highlight: 'Dlhodobá spolupráca',
  },
];

interface TWDeliverablesProps {
  onCtaClick: () => void;
}

export default function TWDeliverables({ onCtaClick }: TWDeliverablesProps) {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          <div
            className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-gift-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-sm font-semibold">Čo dostanete</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 mb-4 leading-tight">
              Nie len pekné stránky —{' '}
              <span className="text-orange-500">nástroj, ktorý predáva</span>
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed">
              Každý web, ktorý odovzdáme, obsahuje tieto výstupy. Bez príplatkov, bez prekvapení.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {deliverables.map((item, i) => (
              <div
                key={item.title}
                className={`group bg-zinc-50 hover:bg-white border border-zinc-100 hover:border-orange-200 rounded-2xl p-6 transition-all duration-500 cursor-default ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-zinc-100 group-hover:border-orange-200 group-hover:bg-orange-50 transition-all duration-300 mb-4">
                  <i className={`${item.icon} text-lg text-zinc-400 group-hover:text-orange-500 transition-colors duration-300`} />
                </div>
                <div className="inline-block bg-orange-50 text-orange-600 text-[10px] font-bold px-2.5 py-1 rounded-full mb-3 uppercase tracking-wide">
                  {item.highlight}
                </div>
                <h3 className="text-zinc-900 font-bold text-sm mb-2 leading-tight">{item.title}</h3>
                <p className="text-zinc-500 text-xs leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          {/* CTA strip */}
          <div
            className={`relative overflow-hidden rounded-2xl transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ background: 'linear-gradient(135deg, #18181b 0%, #27272a 100%)' }}
          >
            <div
              className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 60%)' }}
            />
            <div className="relative px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="text-white font-black text-xl mb-1.5">Všetko toto dostanete v každom balíku</p>
                <p className="text-zinc-400 text-sm">Konzultácia je zadarmo a nezáväzná — len si porozprávame o vašom projekte.</p>
              </div>
              <button
                onClick={onCtaClick}
                className="flex-shrink-0 inline-flex items-center gap-2.5 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
              >
                Chcem konzultáciu zadarmo
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
